import React, { useEffect, useState } from 'react';
import { Field, inputClass } from './BackgroundFields';
import {
  communityPickerPatch,
  fetchHubupCommunitiesForPicker,
  fetchHubupTopicsForPicker,
  fetchNotificationsSnapshot,
  refreshHubupComponentProps,
} from '../utils/hubupWebCreator';

export default function HubupCommunityFields({
  groupId,
  topicId = '',
  showTopic = false,
  showCount = false,
  notificationCount = 3,
  onPatch,
}) {
  const [communities, setCommunities] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loadingCommunities, setLoadingCommunities] = useState(true);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoadingCommunities(true);
    fetchHubupCommunitiesForPicker()
      .then((list) => {
        if (!cancelled) setCommunities(list);
      })
      .catch(() => {
        if (!cancelled) setError('Impossible de charger vos communautés HubUP.');
      })
      .finally(() => {
        if (!cancelled) setLoadingCommunities(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!showTopic || !groupId) {
      setTopics([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingTopics(true);
    fetchHubupTopicsForPicker(groupId)
      .then((list) => {
        if (!cancelled) setTopics(list);
      })
      .finally(() => {
        if (!cancelled) setLoadingTopics(false);
      });
    return () => {
      cancelled = true;
    };
  }, [groupId, showTopic]);

  const handleCommunityChange = async (nextGroupId) => {
    const community = communities.find((c) => c.id === nextGroupId);
    if (!community) {
      onPatch(communityPickerPatch(null, { topicId: '', cachedNotifications: [] }));
      return;
    }
    const base = communityPickerPatch(community, { topicId: '' });
    onPatch(base);
    if (showCount) {
      try {
        const snapshot = await fetchNotificationsSnapshot(
          community.id,
          '',
          notificationCount,
          community,
        );
        onPatch({ ...base, ...snapshot });
      } catch {
        /* garde la sélection communauté */
      }
    }
  };

  const handleTopicChange = async (nextTopicId) => {
    onPatch({ topicId: nextTopicId });
    if (!groupId || !showCount) return;
    const community = communities.find((c) => c.id === groupId);
    try {
      const snapshot = await fetchNotificationsSnapshot(
        groupId,
        nextTopicId,
        notificationCount,
        community,
      );
      onPatch({ topicId: nextTopicId, ...snapshot });
    } catch {
      /* ignore */
    }
  };

  const handleRefreshSnapshot = async () => {
    if (!groupId) return;
    try {
      const props = await refreshHubupComponentProps({
        type: showCount ? 'hubupNotifications' : 'hubupQr',
        props: { groupId, topicId, notificationCount },
      });
      onPatch(props);
    } catch {
      setError('Impossible de rafraîchir les données HubUP.');
    }
  };

  return (
    <div className="space-y-3">
      {error ? <p className="text-xs text-destructive">{error}</p> : null}

      <Field label="Communauté HubUP">
        <select
          className={inputClass}
          value={groupId || ''}
          disabled={loadingCommunities}
          onChange={(e) => handleCommunityChange(e.target.value)}
        >
          <option value="">
            {loadingCommunities ? 'Chargement…' : 'Choisir une communauté'}
          </option>
          {communities.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </Field>

      {showTopic && groupId ? (
        <Field label="Rubrique (optionnel)">
          <select
            className={inputClass}
            value={topicId || ''}
            disabled={loadingTopics}
            onChange={(e) => handleTopicChange(e.target.value)}
          >
            <option value="">
              {loadingTopics ? 'Chargement…' : 'Toutes les rubriques'}
            </option>
            {topics.map((t) => (
              <option key={t.id} value={t.id}>
                {t.title}
              </option>
            ))}
          </select>
        </Field>
      ) : null}

      {showCount ? (
        <Field label="Nombre de notifications">
          <select
            className={inputClass}
            value={String(notificationCount || 3)}
            onChange={async (e) => {
              const nextCount = Number(e.target.value);
              onPatch({ notificationCount: nextCount });
              if (!groupId) return;
              const community = communities.find((c) => c.id === groupId);
              try {
                const snapshot = await fetchNotificationsSnapshot(
                  groupId,
                  topicId || '',
                  nextCount,
                  community,
                );
                onPatch({ notificationCount: nextCount, ...snapshot });
              } catch {
                /* ignore */
              }
            }}
          >
            <option value="1">1 dernière</option>
            <option value="2">2 dernières</option>
            <option value="3">3 dernières</option>
          </select>
        </Field>
      ) : null}

      {groupId ? (
        <button
          type="button"
          className="text-xs text-primary hover:underline"
          onClick={handleRefreshSnapshot}
        >
          Rafraîchir les données HubUP (QR inclus si le logo a changé)
        </button>
      ) : null}

      {!loadingCommunities && communities.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Aucune communauté HubUP accessible avec votre compte.
        </p>
      ) : null}
    </div>
  );
}
